import type { BotPartSlot } from '../entities/bot-part-slots';
import type { PlayerGrouping } from '../game-settings/player-grouping';
import type { ICoord2D } from '../geometry/shapes';

export enum DisconnectReason {
  menuOption = 'menuOption',
  browser = 'browser',
  AFK = 'AFK',
  pagsExpired = 'pagsExpired'
}

export enum EngineType {
  GES = 'ges',
  LOCAL = 'local'
}

export interface PartAnalytics {
  gear: string;
  swatch: string;
}

export type LoadoutAnalytics = Record<BotPartSlot, PartAnalytics>;

export interface IBoostAnalytics {
  boostId: string;
  selectBoostOnPurchase: boolean;
}

export interface IMatchPlayerSkillUsage {
  skillType: string;
  usageCount: number;
}

export interface IMatchPlayerDisconnectionEvent {
  reason: DisconnectReason;
  timeStamp: Date;
  location: ICoord2D;
}

export interface IMatchPlayerDeviceInfoAnalytics {
  platform: string;
  browser: string;
  version: string;
}

export interface IMatchPlayerDeathRecord {
  deathNumber: number;
  location: ICoord2D;
  timeStamp: Date;
  killingBlowEntity: string;
  damageType: string;
}

/** Death fields recorded at the call site; deathNumber is assigned by the combat analytics manager. */
export type IMatchPlayerDeathDetails = Omit<IMatchPlayerDeathRecord, 'deathNumber'>;

export interface IMatchPlayerCombatStats {
  timesInCombat: number;
  totalCombatDurationSeconds: number;
}

/**
 * Shared match header for the full analytics JSON blob (Azure upload).
 * Mode-specific blobs extend this.
 */
export interface IMatchFullAnalyticsBase {
  matchId: string;
  matchMode: PlayerGrouping;
  engineType: EngineType;
  startDateTime: Date;
  endDateTime: Date;
  durationSeconds: number;
  mapId: string;
  players: IMatchPlayerFullAnalyticsBase[];
}

/**
 * Fields every mode puts on a full-blob player row.
 * Combat kills/deaths/time and linear tutorial steps live on {@link IMatchPlayerCombatAnalytics}.
 * Heal-orb usage stays on the Zone Rush player row.
 */
export interface IMatchPlayerFullAnalyticsBase {
  playerId: string;
  teamID: number;
  aiPlayer: boolean;
  /** -1: loss, 0: tie, 1: win */
  matchOutcome: number;
  loadout: Record<BotPartSlot, PartAnalytics>;
  boost?: IBoostAnalytics;
  skillsUsed: IMatchPlayerSkillUsage[];
  disconnectionEvents: IMatchPlayerDisconnectionEvent[];
  deviceInfo: IMatchPlayerDeviceInfoAnalytics;
}

/** Combat player fields shared by Zone Rush and Outpost Run (not Terraforming). */
export interface IMatchPlayerCombatAnalytics extends IMatchPlayerFullAnalyticsBase {
  kills: number;
  deaths: IMatchPlayerDeathRecord[];
  combatStats: IMatchPlayerCombatStats;
  /** Last linear tutorial step name triggered this match (OR / ZR). Empty string if none. */
  tutorialStep: string;
}

/** Compact match close payload for SQL `game_end_analysis` (not the full blob). */
export interface IMatchAnalytics {
  matchId: string;
  mapId: string;
  startDateTime: Date;
  endDateTime: Date;
  players: IMatchPlayerAnalytics[];
}

/** Compact player row inside {@link IMatchAnalytics}. */
export interface IMatchPlayerAnalytics {
  playerId: string;
  teamID: number;
  /** Wire int: 1 win, 0 tie, -1 loss (same values as network MatchOutcomeEnum). */
  matchOutcome: number;
  loadout: Record<BotPartSlot, PartAnalytics>;
}
